/* NEW: UI_REVAMP */
import { Box, DrawerBody, DrawerFooter, Flex, Grid, Text, useRadioGroup } from '@chakra-ui/react';
import React from 'react';
import GoldPatternButton from '../../button/GoldPatternButton';
import DateRangeCalendarStage, { DateRangeInput } from '../../filter/DateRangeCalendarStage';
import { SiteFilterOptionCard, StatusFilterOptionCard } from '../../filter/FilterOptionCards';
import {
	getDefaultTransactionDateRange,
	HISTORY_SITE_OPTIONS,
	HISTORY_STATUS_OPTIONS,
} from '../../history/filter/historyFilterConstants';
import { createCalendarConfirmHandler } from './dateRangeFilterUtils';
import { DesktopFilterContainer, MobileFilterContainer } from './FilterContainers';
import { useHistoryFilterState } from './useFilterState';

const useHistoryFilterContent = ({
	isMobile = false,
	selected,
	maxDate,
	childSite,
	setChildSite,
	childStatus,
	setChildStatus,
	childDateRange,
	setChildDateRange,
	draftDateRange,
	setDraftDateRange,
	isCalendarView,
	setIsCalendarView,
	setSiteFilter,
	setTicketStatus,
	onChangeDateRange,
	onClose,
}) => {
	const { getRootProps: getSiteRootProps, getRadioProps: getSiteRadioProps } = useRadioGroup({
		name: isMobile ? 'historySiteMobile' : 'historySite',
		value: childSite,
		onChange: (value) => setChildSite(value),
	});
	const { getRootProps: getStatusRootProps, getRadioProps: getStatusRadioProps } = useRadioGroup({
		name: isMobile ? 'historyStatusMobile' : 'historyStatus',
		value: childStatus,
		onChange: (value) => setChildStatus(value),
	});

	const siteRow = getSiteRootProps();
	const statusRow = getStatusRootProps();

	const handleCalendarReset = () => {
		setDraftDateRange(getDefaultTransactionDateRange({ selected }));
		setIsCalendarView(false);
	};

	const handleCalendarConfirm = createCalendarConfirmHandler({
		draftDateRange,
		selected,
		setChildDateRange,
		setDraftDateRange,
		setIsCalendarView,
	});

	const handleReset = () => {
		setChildSite('All');
		setChildStatus('All');
		const resetDate = selected || new Date();
		setChildDateRange([resetDate, resetDate]);
		setDraftDateRange([resetDate, resetDate]);
		onChangeDateRange?.(resetDate, resetDate);
		setSiteFilter('All');
		setTicketStatus('All');
		onClose();
	};

	const handleConfirm = () => {
		const selectedStartDate = childDateRange[0] || selected || new Date();
		const selectedEndDate = childDateRange[1] || selectedStartDate;
		onChangeDateRange?.(selectedStartDate, selectedEndDate);
		setSiteFilter(childSite);
		setTicketStatus(childStatus);
		onClose();
	};

	const content = isCalendarView ? (
		<DateRangeCalendarStage
			label='Draw Date'
			dateRange={draftDateRange}
			onChangeDateRange={setDraftDateRange}
			maxDate={maxDate}
			isMobile={isMobile}
			clearLabel='Reset'
			isTextClearButton={!isMobile}
			textClearButtonPadding={!isMobile ? '0 12px' : undefined}
			onClear={handleCalendarReset}
			onConfirm={handleCalendarConfirm}
		/>
	) : (
		<>
			<Text
				fontSize={isMobile ? '14px' : '16px'}
				fontWeight='500'
				color='#111111'
				marginBottom='2px'
			>
				Draw Date
			</Text>
			<DateRangeInput
				startDate={childDateRange[0]}
				endDate={childDateRange[1]}
				onClick={() => {
					setDraftDateRange(childDateRange);
					setIsCalendarView(true);
				}}
			/>

			<Text
				fontSize={isMobile ? '14px' : '16px'}
				fontWeight='500'
				color='#111111'
				marginTop='12px'
				marginBottom='2px'
			>
				Site
			</Text>
			<Flex
				{...siteRow}
				background='#FFFFFF'
				borderRadius={isMobile ? '14px' : '8px'}
				gap='2px'
				boxShadow='sm'
			>
				{HISTORY_SITE_OPTIONS.map((value) => {
					const radio = getSiteRadioProps({ value });
					return (
						<SiteFilterOptionCard
							key={value}
							{...radio}
							isSelected={childSite === value}
							height={isMobile ? undefined : '40px'}
							borderRadius={isMobile ? undefined : '6px'}
							fontSize={isMobile ? '14px' : '16px'}
							fontWeight='600'
						>
							{value}
						</SiteFilterOptionCard>
					);
				})}
			</Flex>

			<Text
				fontSize={isMobile ? '14px' : '16px'}
				fontWeight='500'
				color='#111111'
				marginTop='12px'
				marginBottom='2px'
			>
				Ticket Status
			</Text>
			<Grid templateColumns='repeat(3, 1fr)' gap='4px' {...statusRow} textAlign='center'>
				{HISTORY_STATUS_OPTIONS.map((option) => {
					const radio = getStatusRadioProps({ value: option.value });
					return (
						<StatusFilterOptionCard
							key={option.value}
							{...radio}
							isSelected={childStatus === option.value}
							fontSize={isMobile ? '14px' : '16px'}
						>
							{option.label}
						</StatusFilterOptionCard>
					);
				})}
			</Grid>
		</>
	);

	const actions = isCalendarView ? null : isMobile ? (
		<Flex gap='8px' width='100%' alignItems='center'>
			<Box
				as='button'
				flex='0 0 auto'
				padding='0 18px'
				fontSize='16px'
				fontWeight='700'
				color='#FF5A00'
				lineHeight='100%'
				onClick={handleReset}
			>
				Reset
			</Box>
			<GoldPatternButton flex='1' height='52px' onClick={handleConfirm}>
				Confirm
			</GoldPatternButton>
		</Flex>
	) : (
		<Box marginTop='auto' padding='16px 8px 0'>
			<Flex justifyContent='space-between' alignItems='center'>
				<Box
					as='button'
					flex='0 0 auto'
					padding='0 16px'
					fontSize='16px'
					fontWeight='700'
					color='#FF5A00'
					lineHeight='100%'
					onClick={handleReset}
				>
					Reset
				</Box>
				<GoldPatternButton flex='1' height='52px' onClick={handleConfirm}>
					Confirm
				</GoldPatternButton>
			</Flex>
		</Box>
	);

	return { content, actions };
};

export const HistoryFilterPanel = ({
	isOpen,
	onClose,
	siteFilter,
	setSiteFilter,
	ticketStatus,
	setTicketStatus,
	selected,
	startDate,
	endDate,
	onChangeDateRange,
	maxDate,
}) => {
	const historyFilterState = useHistoryFilterState({
		isOpen,
		siteFilter,
		ticketStatus,
		selected,
		startDate,
		endDate,
	});
	const { content, actions } = useHistoryFilterContent({
		selected,
		maxDate,
		setSiteFilter,
		setTicketStatus,
		onChangeDateRange,
		onClose,
		...historyFilterState,
	});

	return (
		<DesktopFilterContainer
			isOpen={isOpen}
			isCalendarView={historyFilterState.isCalendarView}
			onClose={onClose}
		>
			{content}
			{actions}
		</DesktopFilterContainer>
	);
};

export const MobileHistoryFilterSheet = ({
	isOpen,
	onClose,
	siteFilter,
	setSiteFilter,
	ticketStatus,
	setTicketStatus,
	selected,
	startDate,
	endDate,
	onChangeDateRange,
	maxDate,
}) => {
	const historyFilterState = useHistoryFilterState({
		isOpen,
		siteFilter,
		ticketStatus,
		selected,
		startDate,
		endDate,
	});
	const { content, actions } = useHistoryFilterContent({
		isMobile: true,
		selected,
		maxDate,
		setSiteFilter,
		setTicketStatus,
		onChangeDateRange,
		onClose,
		...historyFilterState,
	});

	return (
		<MobileFilterContainer isOpen={isOpen} onClose={onClose}>
			<DrawerBody padding='0'>{content}</DrawerBody>
			<DrawerFooter padding='12px 0 0 0'>{actions}</DrawerFooter>
		</MobileFilterContainer>
	);
};
